<template>
  <div id="contenedor-main-financiero-consultas">
    <v-skeleton-loader
      v-if="$apollo.loading"
      type="article, article">
    </v-skeleton-loader>

    <v-container v-else fluid class="px-6 py-6">
      <!-- Header Section -->
      <v-row class="mb-6" align="center">
        <v-col cols="12" md="6">
          <div class="header-section">
            <h1 class="text-h4 font-weight-bold mb-1">
              Dashboard Financiero
            </h1>
            <p class="text-subtitle-1 text--secondary mb-0">
              Análisis de presupuesto autorizado, comprometido y ejecutado
            </p>
          </div>
        </v-col>
        <v-col cols="12" md="6" class="text-md-right">
          <v-select
            v-model="filtros.ejercicio"
            :items="opcionesEjercicio"
            label="Filtrar por ejercicio"
            item-text="label"
            item-value="value"
            dense
            hide-details
            outlined
            prepend-inner-icon="mdi-calendar"
            class="selector-ejercicio"
            style="max-width: 280px; display: inline-block" />
        </v-col>
      </v-row>

      <!-- KPI Cards Section -->
      <v-row class="mb-6">
        <v-col
          v-for="(kpi, index) in tarjetasKpi"
          :key="index"
          cols="12"
          sm="6"
          md="4"
          lg="3"
          class="d-flex">
          <v-card
            :class="['card-kpi', kpi.clase]"
            elevation="2"
            hover
            style="width: 100%">
            <v-card-text class="pa-5 d-flex flex-column" style="height: 100%">
              <div class="d-flex align-start mb-3">
                <v-avatar :color="kpi.color" size="48" class="mr-3 flex-shrink-0">
                  <v-icon color="white">{{ kpi.icono }}</v-icon>
                </v-avatar>
                <div class="flex-grow-1">
                  <div class="d-flex align-center mb-1">
                    <span class="text-caption text--secondary text-uppercase"
                      style="font-weight: 500; letter-spacing: 0.5px">
                      {{ kpi.titulo }}
                    </span>
                    <v-tooltip bottom max-width="260">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon
                          v-bind="attrs"
                          v-on="on"
                          small
                          class="ml-1"
                          color="grey lighten-1">
                          mdi-information-outline
                        </v-icon>
                      </template>
                      <span>{{ kpi.tooltip }}</span>
                    </v-tooltip>
                  </div>
                  <div class="text-h6 font-weight-bold">
                    {{ kpi.valor | currency }}
                  </div>
                </div>
              </div>
              <div class="mt-auto">
                <v-chip
                  v-if="kpi.etiqueta"
                  :color="kpi.colorEtiqueta"
                  small
                  text-color="white"
                  class="font-weight-bold">
                  {{ kpi.etiqueta }}
                </v-chip>
                <div v-else style="height: 24px"></div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Table Section -->
      <v-card elevation="2" class="tabla-financiera">
        <v-card-title class="py-4 px-6 primary white--text">
          <v-icon color="white" class="mr-2">mdi-table-large</v-icon>
          <span class="text-h6">Resumen Detallado por Ejercicio</span>
        </v-card-title>

        <v-card-text class="pa-0">
          <v-data-table
            :headers="headers"
            :items="filasFiltradas"
            :items-per-page="15"
            class="tabla-moderna elevation-0">
            <template v-slot:[`item.ejercicio`]="{ item }">
              <span class="font-weight-bold text-h6 primary--text">
                {{ item.ejercicio }}
              </span>
            </template>

            <template v-slot:[`item.autorizado`]="{ item }">
              <div class="text-right font-weight-medium">
                {{ item.autorizado | currency }}
              </div>
            </template>

            <template v-slot:[`item.comprometido`]="{ item }">
              <div class="text-right font-weight-medium">
                {{ item.comprometido | currency }}
              </div>
            </template>

            <template v-slot:[`item.devengado`]="{ item }">
              <div class="text-right font-weight-medium">
                {{ item.devengado | currency }}
              </div>
            </template>

            <template v-slot:[`item.pagado`]="{ item }">
              <div class="text-right font-weight-medium">
                {{ item.pagado | currency }}
              </div>
            </template>

            <template v-slot:[`item.porEjecutar`]="{ item }">
              <div class="text-right">
                <v-chip
                  small
                  :color="item.porEjecutar > 0 ? 'orange lighten-4' : 'grey lighten-3'"
                  :text-color="item.porEjecutar > 0 ? 'orange darken-2' : 'grey darken-1'">
                  {{ item.porEjecutar | currency }}
                </v-chip>
              </div>
            </template>

            <template v-slot:[`item.economiaAdjudicacion`]="{ item }">
              <div class="text-right">
                <v-chip
                  small
                  :color="item.economiaAdjudicacion >= 0 ? 'green lighten-4' : 'red lighten-4'"
                  :text-color="item.economiaAdjudicacion >= 0 ? 'green darken-2' : 'red darken-2'"
                  class="font-weight-bold">
                  <v-icon
                    small
                    left
                    :color="item.economiaAdjudicacion >= 0 ? 'green darken-2' : 'red darken-2'">
                    {{ item.economiaAdjudicacion >= 0 ? 'mdi-trending-up' : 'mdi-trending-down' }}
                  </v-icon>
                  {{ item.economiaAdjudicacion | currency }}
                </v-chip>
              </div>
            </template>

            <template v-slot:[`item.economiaPago`]="{ item }">
              <div class="text-right">
                <v-chip
                  small
                  :color="item.economiaPago >= 0 ? 'green lighten-4' : 'red lighten-4'"
                  :text-color="item.economiaPago >= 0 ? 'green darken-2' : 'red darken-2'"
                  class="font-weight-bold">
                  <v-icon
                    small
                    left
                    :color="item.economiaPago >= 0 ? 'green darken-2' : 'red darken-2'">
                    {{ item.economiaPago >= 0 ? 'mdi-trending-up' : 'mdi-trending-down' }}
                  </v-icon>
                  {{ item.economiaPago | currency }}
                </v-chip>
              </div>
            </template>

            <template v-slot:[`item.avanceFinanciero`]="{ item }">
              <div class="text-center px-2">
                <v-progress-circular
                  :value="item.avanceFinanciero > 100 ? 100 : item.avanceFinanciero"
                  :color="obtenerColorAvance(item.avanceFinanciero)"
                  :rotate="-90"
                  size="50"
                  width="5">
                  <span class="text-caption font-weight-bold">
                    {{ $numberRound(item.avanceFinanciero) }}%
                  </span>
                </v-progress-circular>
              </div>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-container>
  </div>
</template>

<script>
import CONTRATOS from '@/graphql/querys/administracion/reportes/financiero/contratosFinanciero.gql';
import OFICIOS from '@/graphql/querys/administracion/reportes/financiero/oficiosFinanciero.gql';

const EJERCICIO_TODOS = 'TODOS';

export default {
  name: 'MainFinancieroConsultas',
  data() {
    return {
      filtros: {
        ejercicio: EJERCICIO_TODOS,
      },
      headers: [
        {
          text: 'Ejercicio',
          value: 'ejercicio',
          align: 'center',
          sortable: true,
        },
        {
          text: 'Autorizado Neto',
          value: 'autorizado',
          align: 'end',
          sortable: true,
        },
        {
          text: 'Comprometido',
          value: 'comprometido',
          align: 'end',
          sortable: true,
        },
        {
          text: 'Devengado',
          value: 'devengado',
          align: 'end',
          sortable: true,
        },
        {
          text: 'Pagado',
          value: 'pagado',
          align: 'end',
          sortable: true,
        },
        {
          text: 'Por Ejecutar',
          value: 'porEjecutar',
          align: 'end',
          sortable: true,
        },
        {
          text: 'Economía Adj.',
          value: 'economiaAdjudicacion',
          align: 'end',
          sortable: true,
        },
        {
          text: 'Economía Pago',
          value: 'economiaPago',
          align: 'end',
          sortable: true,
        },
        {
          text: 'Avance',
          value: 'avanceFinanciero',
          align: 'center',
          sortable: true,
        },
      ],
    };
  },
  apollo: {
    contratos: {
      query: CONTRATOS,
    },
    oficiosFinancieros: {
      query: OFICIOS,
      update(data) {
        return data.filtrarOficiosAutorizacion;
      },
      variables() {
        return {
          filtros: {
            limite: 5000,
            pagina: 1,
            ordenarPor: 'fecha',
            direccion: 'DESC',
          },
        };
      },
    },
  },
  computed: {
    opcionesEjercicio() {
      return [
        { label: 'Todos', value: EJERCICIO_TODOS },
        ...this.filasPorEjercicio.map(({ ejercicio }) => ({
          label: `${ejercicio}`,
          value: ejercicio,
        })),
      ];
    },
    filasFiltradas() {
      if (this.filtros.ejercicio === EJERCICIO_TODOS) return this.filasPorEjercicio;
      return this.filasPorEjercicio.filter(({ ejercicio }) => ejercicio === this.filtros.ejercicio);
    },
    tarjetasKpi() {
      const totales = this.filasFiltradas.reduce((acc, fila) => ({
        autorizado: acc.autorizado + fila.autorizado,
        comprometido: acc.comprometido + fila.comprometido,
        devengado: acc.devengado + fila.devengado,
        pagado: acc.pagado + fila.pagado,
        porEjecutar: acc.porEjecutar + fila.porEjecutar,
        economiaAdjudicacion: acc.economiaAdjudicacion + fila.economiaAdjudicacion,
        economiaPago: acc.economiaPago + fila.economiaPago,
      }), {
        autorizado: 0,
        comprometido: 0,
        devengado: 0,
        pagado: 0,
        porEjecutar: 0,
        economiaAdjudicacion: 0,
        economiaPago: 0,
      });

      return [
        {
          titulo: 'Autorizado Neto',
          valor: totales.autorizado,
          icono: 'mdi-shield-check',
          color: 'blue darken-1',
          clase: 'card-autorizado',
          tooltip: 'Suma de todos los oficios de autorización de presupuesto, restando los oficios de cancelación.',
        },
        {
          titulo: 'Comprometido',
          valor: totales.comprometido,
          icono: 'mdi-file-document-edit',
          color: 'purple darken-1',
          clase: 'card-comprometido',
          etiqueta: totales.comprometido > totales.autorizado ? 'Sobre presupuesto' : null,
          colorEtiqueta: 'red darken-1',
          tooltip: 'Suma del importe total de cada contrato firmado (total del contrato + modificaciones convenidas).',
        },
        {
          titulo: 'Devengado',
          valor: totales.devengado,
          icono: 'mdi-hammer-wrench',
          color: 'cyan darken-1',
          clase: 'card-devengado',
          tooltip: 'Obra ejecutada y estimada: suma de estimaciones + escalatorias + gastos extraordinarios de todos los contratos.',
        },
        {
          titulo: 'Pagado',
          valor: totales.pagado,
          icono: 'mdi-cash-check',
          color: 'green darken-1',
          clase: 'card-pagado',
          tooltip: 'Dinero efectivamente transferido a los contratistas. Suma de todos los pagos registrados en solicitudes de pago.',
        },
        {
          titulo: 'Por Ejecutar',
          valor: totales.porEjecutar,
          icono: 'mdi-progress-clock',
          color: 'orange darken-1',
          clase: 'card-ejecutar',
          tooltip: 'Obra pendiente de ejecutar. Fórmula: Comprometido − Devengado.',
        },
        {
          titulo: 'Economía Adjudicación',
          valor: totales.economiaAdjudicacion,
          icono: totales.economiaAdjudicacion >= 0 ? 'mdi-thumb-up' : 'mdi-alert-circle',
          color: totales.economiaAdjudicacion >= 0 ? 'green darken-1' : 'red darken-1',
          clase: totales.economiaAdjudicacion >= 0 ? 'card-economia-positiva' : 'card-economia-negativa',
          etiqueta: totales.economiaAdjudicacion >= 0 ? 'Ahorro' : 'Déficit',
          colorEtiqueta: totales.economiaAdjudicacion >= 0 ? 'green darken-1' : 'red darken-1',
          tooltip: 'Diferencia entre lo autorizado y lo contratado. Fórmula: Autorizado Neto − Comprometido. Negativo indica que se contrató más de lo autorizado.',
        },
        {
          titulo: 'Economía Pago',
          valor: totales.economiaPago,
          icono: totales.economiaPago >= 0 ? 'mdi-thumb-up' : 'mdi-alert-circle',
          color: totales.economiaPago >= 0 ? 'green darken-1' : 'red darken-1',
          clase: totales.economiaPago >= 0 ? 'card-economia-positiva' : 'card-economia-negativa',
          etiqueta: totales.economiaPago >= 0 ? 'Ahorro' : 'Déficit',
          colorEtiqueta: totales.economiaPago >= 0 ? 'green darken-1' : 'red darken-1',
          tooltip: 'Presupuesto autorizado pendiente de pagar. Fórmula: Autorizado Neto − Pagado.',
        },
      ];
    },
    filasPorEjercicio() {
      const mapa = {};

      (this.contratos || []).forEach((contrato) => {
        const ejercicio = Number(contrato.ejercicio)
          || this.obtenerEjercicio(contrato.fecha);

        if (!mapa[ejercicio]) {
          mapa[ejercicio] = {
            autorizado: 0,
            comprometido: 0,
            devengado: 0,
            pagado: 0,
            contratos: 0,
          };
        }

        const totalModificado = this.toNumber(
          contrato.modificado && contrato.modificado.total,
        );
        const comprometido = this.toNumber(contrato.total) + totalModificado;
        const devengado = this.sumArray(contrato.estimaciones, 'importeEstimado')
          + this.sumArray(contrato.escalatorias, 'importe')
          + this.sumArray(contrato.gastos, 'importe');

        const pagado = (contrato.solicitudesPagos || [])
          .map(({ detalles = [] }) => detalles)
          .flat()
          .map(({ pagosDetalles = [] }) => pagosDetalles)
          .flat()
          .reduce(
            (acumulado, detallePago) => acumulado + this.toNumber(detallePago.importe),
            0,
          );

        mapa[ejercicio].comprometido += comprometido;
        mapa[ejercicio].devengado += devengado;
        mapa[ejercicio].pagado += pagado;
        mapa[ejercicio].contratos += 1;
      });

      const oficios = (this.oficiosFinancieros && this.oficiosFinancieros.datos) || [];

      oficios.forEach((oficio) => {
        const ejercicio = this.obtenerEjercicio(oficio.fecha);

        if (!mapa[ejercicio]) {
          mapa[ejercicio] = {
            autorizado: 0,
            comprometido: 0,
            devengado: 0,
            pagado: 0,
            contratos: 0,
          };
        }

        const tipoNormalizado = this.normalizarTexto(oficio.tipo);
        const signo = tipoNormalizado.includes('CANCELACION') ? -1 : 1;
        mapa[ejercicio].autorizado += (signo * this.toNumber(oficio.importe));
      });

      return Object.keys(mapa)
        .map((ejercicio) => {
          const fila = mapa[ejercicio];
          const porEjecutar = fila.comprometido - fila.devengado;
          const economiaAdjudicacion = fila.autorizado - fila.comprometido;
          const economiaPago = fila.autorizado - fila.pagado;
          const avanceFinanciero = fila.comprometido > 0
            ? ((fila.devengado / fila.comprometido) * 100)
            : 0;

          return {
            ejercicio: Number(ejercicio),
            ...fila,
            porEjecutar,
            economiaAdjudicacion,
            economiaPago,
            avanceFinanciero,
          };
        })
        .sort((a, b) => b.ejercicio - a.ejercicio);
    },
  },
  methods: {
    toNumber(valor) {
      const numero = Number(valor);
      return Number.isFinite(numero) ? numero : 0;
    },
    sumArray(lista = [], propiedad) {
      return lista.reduce((acumulado, item) => acumulado + this.toNumber(item[propiedad]), 0);
    },
    obtenerEjercicio(fecha) {
      if (!fecha) return 0;
      const date = new Date(fecha);
      if (Number.isNaN(date.getTime())) return 0;
      return date.getFullYear();
    },
    normalizarTexto(valor = '') {
      return String(valor)
        .toUpperCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');
    },
    obtenerColorAvance(porcentaje) {
      if (porcentaje >= 80) return 'green';
      if (porcentaje >= 50) return 'orange';
      if (porcentaje >= 25) return 'deep-orange';
      return 'red';
    },
  },
};
</script>

<style lang="scss" scoped>
#contenedor-main-financiero-consultas {
  background: #f5f7fa;
  min-height: 100vh;

  .header-section {
    h1 {
      color: #1a237e;
      letter-spacing: -0.5px;
    }

    p {
      color: #546e7a;
    }
  }

  .selector-ejercicio {
    background: white;
    border-radius: 8px;
  }

  // KPI Cards Styling
  .card-kpi {
    border-radius: 12px !important;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    border-left: 4px solid transparent;
    background: white;

    &:hover {
      transform: translateY(-4px);
      box-shadow: 0 12px 24px rgba(0, 0, 0, 0.12) !important;
    }

    &.card-autorizado {
      border-left-color: #1976d2;
    }

    &.card-comprometido {
      border-left-color: #7b1fa2;
    }

    &.card-devengado {
      border-left-color: #0097a7;
    }

    &.card-pagado {
      border-left-color: #388e3c;
    }

    &.card-ejecutar {
      border-left-color: #f57c00;
    }

    &.card-economia-positiva {
      border-left-color: #388e3c;
      background: linear-gradient(135deg, #ffffff 0%, #f1f8f4 100%);
    }

    &.card-economia-negativa {
      border-left-color: #d32f2f;
      background: linear-gradient(135deg, #ffffff 0%, #fef5f5 100%);
    }

    .text-caption {
      font-size: 0.75rem;
      font-weight: 500;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    .text-h6 {
      line-height: 1.2;
      color: #263238;
    }
  }

  // Table Styling
  .tabla-financiera {
    border-radius: 12px !important;
    overflow: hidden;

    .v-card__title {
      border-radius: 12px 12px 0 0;
    }

    .tabla-moderna {
      ::v-deep {
        .v-data-table-header {
          background: #fafafa;

          th {
            font-weight: 700 !important;
            font-size: 0.875rem !important;
            color: #37474f !important;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            border-bottom: 2px solid #e0e0e0 !important;
          }
        }

        tbody tr {
          transition: background 0.2s ease;

          &:hover {
            background: #f5f7fa !important;
          }

          td {
            font-size: 0.875rem;
            padding: 16px 12px !important;
            border-bottom: 1px solid #eeeeee !important;
          }
        }

        .v-chip {
          font-weight: 600;
          border-radius: 6px;

          .v-icon {
            font-size: 16px;
          }
        }

        .v-progress-circular {
          font-size: 12px;
          font-weight: 700;
        }
      }
    }
  }
}
</style>
